import React from 'react';
import { motion } from 'framer-motion';
import { Car, Truck, Bike, Sun, Snowflake, Repeat, FileText } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { trackTireConfiguratorStep, trackCTAClick } from '../../lib/analytics';

interface TireConfig {
  type: string;
  brand: string;
  season: string;
  quantity: string;
  width: string;
  height: string;
  diameter: string;
  loadIndex: string;
  speedIndex: string;
  runflat: boolean;
  xl: boolean;
  reseller: boolean;
}

interface TireConfiguratorProps {
  config: TireConfig;
  onConfigChange: (key: keyof TireConfig, value: string | boolean) => void;
  onRequestQuote: () => void;
}

const TireConfigurator: React.FC<TireConfiguratorProps> = ({ config, onConfigChange, onRequestQuote }) => {
  const { t, translations } = useLanguage();

  const tireTypes = [
    { value: 'tourisme', label: t('tires.tireTypes.tourisme'), icon: Car },
    { value: 'utilitaire', label: t('tires.tireTypes.utilitaire'), icon: Truck },
    { value: 'moto', label: t('tires.tireTypes.moto'), icon: Bike },
    { value: '4x4', label: t('tires.tireTypes.4x4'), icon: Car },
    { value: 'poids-lourd', label: t('tires.tireTypes.poids-lourd'), icon: Truck }
  ];

  const brands = translations.tires?.availableBrands || [];

  const seasons = [
    { value: 'ete', label: t('tires.seasons.ete'), icon: Sun },
    { value: 'hiver', label: t('tires.seasons.hiver'), icon: Snowflake },
    { value: '4-saisons', label: t('tires.seasons.4-saisons'), icon: Repeat }
  ];

  const widths = Array.from({length: 35}, (_, i) => (145 + i * 10).toString());
  const heights = ['30', '35', '40', '45', '50', '55', '60', '65', '70', '75', '80', '85'];
  const diameters = Array.from({length: 11}, (_, i) => (13 + i).toString());
  const loadIndices = Array.from({length: 50}, (_, i) => (70 + i).toString());
  const speedIndices = ['L', 'M', 'N', 'P', 'Q', 'R', 'S', 'T', 'U', 'H', 'V', 'W', 'Y', 'ZR'];

  return (
    <section className="py-16">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="bg-white rounded-3xl shadow-2xl overflow-hidden"
        >
          <div className="bg-gradient-to-r from-red-600 to-red-700 p-8">
            <h2 className="text-3xl font-bold text-white mb-2">{t('tires.configurator.title')}</h2>
            <p className="text-red-100">{t('tires.configurator.subtitle')}</p>
          </div>

          <div className="p-8 space-y-8">
            {/* Type de pneu */}
            <div>
              <label className="block text-lg font-semibold text-gray-900 mb-4">
                {t('tires.configurator.tireType')}
              </label>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                {tireTypes.map((type) => {
                  const IconComponent = type.icon;
                  return (
                    <motion.button
                      key={type.value}
                      onClick={() => {
                        onConfigChange('type', type.value);
                        trackTireConfiguratorStep('tire_type', type.value);
                      }}
                      whileHover={{ scale: 1.05, y: -2 }}
                      whileTap={{ scale: 0.95 }}
                      className={`flex flex-col items-center p-4 rounded-xl border-2 transition-all duration-200 group ${
                        config.type === type.value
                          ? 'border-red-500 bg-red-50 text-red-700 shadow-red-500/20'
                          : 'border-gray-200 hover:border-red-300 hover:bg-gray-50 hover:shadow-red-500/10'
                      }`}
                    >
                      <div className="absolute inset-0 bg-gradient-to-br from-red-600/0 via-red-500/0 to-transparent opacity-0 group-hover:opacity-10 transition-all duration-500 rounded-xl" />
                      <IconComponent className="w-8 h-8 mb-2 mx-auto group-hover:shadow-red-500/40 transition-shadow duration-300" />
                      <span className="text-sm font-medium group-hover:text-red-600 transition-colors">{type.label}</span>
                    </motion.button>
                  );
                })}
              </div>
            </div>

            {/* Marque et Saison */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <label className="block text-lg font-semibold text-gray-900 mb-4">
                  {t('tires.configurator.brand')}
                </label>
                <select
                  value={config.brand}
                  onChange={(e) => {
                    onConfigChange('brand', e.target.value);
                    trackTireConfiguratorStep('brand', e.target.value);
                  }}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-red-500 focus:outline-none transition-colors text-lg"
                >
                  {brands.map((brand) => (
                    <option key={brand} value={brand.toLowerCase()}>
                      {brand}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-lg font-semibold text-gray-900 mb-4">
                  {t('tires.configurator.season')}
                </label>
                <div className="grid grid-cols-3 gap-3">
                  {seasons.map((season) => {
                    const IconComponent = season.icon;
                    return (
                      <motion.button
                        key={season.value}
                        onClick={() => onConfigChange('season', season.value)}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        className={`flex items-center justify-center p-3 rounded-lg border-2 transition-all duration-200 ${
                          config.season === season.value
                            ? 'border-red-500 bg-red-50 text-red-700'
                            : 'border-gray-200 hover:border-red-300'
                        }`}
                      >
                        <IconComponent className="w-5 h-5 mr-2" />
                        <span className="font-medium">{season.label}</span>
                      </motion.button>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Quantité */}
            <div>
              <label className="block text-lg font-semibold text-gray-900 mb-4">
                {t('tires.configurator.quantity')}
              </label>
              <div className="flex space-x-3">
                {['1', '2', '4'].map((qty) => (
                  <motion.button
                    key={qty}
                    onClick={() => onConfigChange('quantity', qty)}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className={`px-6 py-3 rounded-lg border-2 font-semibold transition-all duration-200 ${
                      config.quantity === qty
                        ? 'border-red-500 bg-red-600 text-white'
                        : 'border-gray-200 hover:border-red-300'
                    }`}
                  >
                    {t(`tires.quantities.${qty}`)}
                  </motion.button>
                ))}
              </div>
            </div>

            {/* Dimensions */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('tires.configurator.dimensions')}</h3>
              <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('tires.configurator.width')}
                  </label>
                  <select
                    value={config.width}
                    onChange={(e) => onConfigChange('width', e.target.value)}
                    className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:border-red-500 focus:outline-none"
                  >
                    <option value="">{t('tires.configurator.width')}</option>
                    {widths.map(width => (
                      <option key={width} value={width}>{width}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('tires.configurator.height')}
                  </label>
                  <select
                    value={config.height}
                    onChange={(e) => onConfigChange('height', e.target.value)}
                    className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:border-red-500 focus:outline-none"
                  >
                    <option value="">{t('tires.configurator.height')}</option>
                    {heights.map(height => (
                      <option key={height} value={height}>{height}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('tires.configurator.diameter')}
                  </label>
                  <select
                    value={config.diameter}
                    onChange={(e) => onConfigChange('diameter', e.target.value)}
                    className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:border-red-500 focus:outline-none"
                  >
                    <option value="">{t('tires.configurator.diameter')}</option>
                    {diameters.map(diameter => (
                      <option key={diameter} value={diameter}>{diameter}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('tires.configurator.loadIndex')}
                  </label>
                  <select
                    value={config.loadIndex}
                    onChange={(e) => onConfigChange('loadIndex', e.target.value)}
                    className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:border-red-500 focus:outline-none"
                  >
                    <option value="">{t('tires.configurator.loadIndex')}</option>
                    {loadIndices.map(index => (
                      <option key={index} value={index}>{index}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('tires.configurator.speedIndex')}
                  </label>
                  <select
                    value={config.speedIndex}
                    onChange={(e) => onConfigChange('speedIndex', e.target.value)}
                    className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:border-red-500 focus:outline-none"
                  >
                    <option value="">{t('tires.configurator.speedIndex')}</option>
                    {speedIndices.map(index => (
                      <option key={index} value={index}>{index}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Options spéciales */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('tires.configurator.options')}</h3>
              <div className="space-y-3">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={config.runflat}
                    onChange={(e) => onConfigChange('runflat', e.target.checked)}
                    className="w-5 h-5 text-red-600 border-2 border-gray-300 rounded focus:ring-red-500"
                  />
                  <span className="ml-3 text-gray-700 font-medium">{t('tires.configurator.runflat')}</span>
                </label>
                
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={config.xl}
                    onChange={(e) => onConfigChange('xl', e.target.checked)}
                    className="w-5 h-5 text-red-600 border-2 border-gray-300 rounded focus:ring-red-500"
                  />
                  <span className="ml-3 text-gray-700 font-medium">{t('tires.configurator.xl')}</span>
                </label>
                
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={config.reseller}
                    onChange={(e) => onConfigChange('reseller', e.target.checked)}
                    className="w-5 h-5 text-red-600 border-2 border-gray-300 rounded focus:ring-red-500"
                  />
                  <span className="ml-3 text-gray-700 font-medium">{t('tires.configurator.reseller')}</span>
                </label>
              </div>
            </div>

            {/* Bouton de recherche */}
            <motion.button
              onClick={() => {
                const isComplete = !!(config.width && config.height && config.diameter);
                trackTireConfiguratorStep('quote_request', 'submitted', isComplete);
                trackCTAClick('Request Quote', 'tire_configurator');
                onRequestQuote();
              }}
              type="button"
              whileHover={{ scale: 1.02, y: -2 }}
              whileTap={{ scale: 0.98 }}
              className="w-full flex items-center justify-center space-x-3 py-4 bg-gradient-to-r from-red-600 to-red-700 text-white font-bold text-lg rounded-xl hover:from-red-700 hover:to-red-800 transition-all duration-200 shadow-lg hover:shadow-xl"
            >
              <FileText className="w-6 h-6" />
              <span>{t('common.cta.requestQuote')}</span>
            </motion.button>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default TireConfigurator;